
  import { defineComponent } from 'vue'
  import { mapState, mapActions } from 'pinia'
  import { breakPoint } from '@/constants'
  import { useStore as useUserStore } from '@/stores/user'
  import { useStore as useUIStateStore } from '@/stores/uiState'

  export default defineComponent({
    data() {
      return {
        siteName: process.env.VUE_APP_SITE_NAME,
        description: process.env.VUE_APP_SITE_DESCRIPTION,
        siteUrl: process.env.VUE_APP_SITE_URL,
      }
    },

    computed: {
      ...mapState(useUIStateStore, [ 'hasShownModals', 'hasScrollLockers' ]),
      ...mapState(useUserStore, [ 'targetUser' ]),

      pageName(): string {
        const { path, name } = this.$route
        return path === '/' ? '' : (name as string || '')
      },

      pageTitle(): string {
        const { pageName, siteName, targetUser } = this
        const userName = targetUser.displayName
        const postfix = userName ? `${ userName }の${ siteName }` : siteName
        return pageName ? `${ pageName } | ${ postfix }` : postfix
      },

      pageFullUrl(): string {
        return `${ this.siteUrl }${ this.$route.fullPath }`
      },

      pageCanonicalUrl(): string {
        return `${ this.siteUrl }${ this.$route.path }`
      },
    },

    watch: {
      pageTitle: {
        immediate: true,
        handler: 'updateMetaInfo',
      },

      $route() {
        const uid = (this.$route.query.uid || '') as string
        this.setTargetUser(uid)
      },

      hasShownModals(bool: boolean) {
        document.body.classList.toggle('-hasShownModals', bool)
      },

      hasScrollLockers(bool: boolean) {
        document.body.classList.toggle('-hasScrollLockers', bool)
      },
    },

    async created() {
      this.checkViewMode()
      this.checkDeviceMode()
      await this.fetchAuthorizedUser()
    },

    methods: {
      ...mapActions(useUIStateStore, [ 'updateViewMode', 'updateDeviceType' ]),
      ...mapActions(useUserStore, [ 'fetchAuthorizedUser', 'setTargetUser' ]),

      /**
       * 表示モードの初回判定と継続的判定を開始
       */
      checkViewMode() {
        const mql = window.matchMedia(`(max-width: ${breakPoint}px)`)
        const update = () => this.updateViewMode(mql.matches ? 'mobile' : 'pc')
        update()
        mql.addEventListener('change', update)
      },

      /**
       * デバイス種別を判定
       */
      checkDeviceMode() {
        this.updateDeviceType(screen.width > breakPoint ? 'pc' : 'mobile')
      },

      /**
       * HTML のメタ情報を更新する
       */
      updateMetaInfo() {
        const { pageTitle, description, pageCanonicalUrl } = this

        document.title = pageTitle

        const selectors = {
          title: [
            'meta[property="og:title"]',
            'meta[name="twitter:title"]',
          ],
          description: [
            'meta[property="og:description"]',
            'meta[name="twitter:description"]',
          ],
          canonical: [
            'link[rel="canonical"]',
          ]
        }
        selectors.title.forEach(selector => document.querySelector(selector)?.setAttribute('content', pageTitle))
        selectors.description.forEach(selector => document.querySelector(selector)?.setAttribute('content', description))
        selectors.canonical.forEach(selector => document.querySelector(selector)?.setAttribute('href', pageCanonicalUrl))
      },
    },
  })
